import { useState } from 'react'
import {
  Camera,
  CheckCircle2,
  FileText,
  LoaderCircle,
  Upload,
  X,
} from 'lucide-react'
import type { PreparedReportCardFile } from '@/lib/reportCardPreparation'
import { Checkbox } from '@/components/ui/checkbox'

export default function ImportDropzone({
  preparedFile,
  isPreparingFile,
  isImporting,
  error,
  hasAcceptedTerms,
  onAcceptedTermsChange,
  onSelectedFile,
  onAnalyze,
}: {
  preparedFile: PreparedReportCardFile | null
  isPreparingFile: boolean
  isImporting: boolean
  error: string | null
  hasAcceptedTerms: boolean
  onAcceptedTermsChange: (checked: boolean) => void
  onSelectedFile: (file: File | null) => void
  onAnalyze: () => void
}) {
  const [isDragActive, setIsDragActive] = useState(false)
  const supportsDragAndDrop =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(pointer: coarse)').matches

  return (
    <div
      className={`min-w-0 overflow-x-hidden rounded-xl border-2 border-solid p-5 transition-colors min-[700px]:border-dashed ${
        supportsDragAndDrop && isDragActive
          ? 'border-purple-600 bg-purple-50'
          : 'border-warm-gray-500 bg-warm-gray-100/60'
      }`}
    >
      <input
        id="report-card-file-input"
        type="file"
        accept=".pdf,.heic,.heif,image/png,image/jpeg,image/webp,image/heic,image/heif"
        className="sr-only"
        onChange={(event) => {
          onSelectedFile(event.target.files?.[0] ?? null)
        }}
      />
      <input
        id="report-card-camera-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(event) => {
          onSelectedFile(event.target.files?.[0] ?? null)
        }}
      />
      <label
        htmlFor="report-card-file-input"
        className="block min-w-0 cursor-pointer"
        onDragEnter={(event) => {
          if (!supportsDragAndDrop) {
            return
          }
          event.preventDefault()
          setIsDragActive(true)
        }}
        onDragOver={(event) => {
          if (!supportsDragAndDrop) {
            return
          }
          event.preventDefault()
          setIsDragActive(true)
        }}
        onDragLeave={(event) => {
          if (!supportsDragAndDrop) {
            return
          }
          event.preventDefault()
          const nextTarget = event.relatedTarget
          if (
            nextTarget instanceof Node &&
            event.currentTarget.contains(nextTarget)
          ) {
            return
          }
          setIsDragActive(false)
        }}
        onDrop={(event) => {
          if (!supportsDragAndDrop) {
            return
          }
          event.preventDefault()
          setIsDragActive(false)
          onSelectedFile(event.dataTransfer.files?.[0] ?? null)
        }}
      >
        <div className="flex min-w-0 flex-col items-center justify-center rounded-lg bg-white/80 px-6 py-10 text-center shadow-[0_2px_4px_0_rgba(0,0,0,0.04)]">
          {isImporting ? (
            <>
              <div className="flex size-14 items-center justify-center rounded-full bg-purple-100 text-purple-800">
                <LoaderCircle className="size-6 animate-spin" />
              </div>
              <div className="mt-4 text-sm font-semibold sm:text-base">Leser dokumentet</div>
              <div className="mt-2 max-w-[28rem] text-xs text-warm-gray-800 sm:text-sm">
                Vi henter ut fag og karakterer fra fila nå. Dette kan ta litt tid
                for store PDF-er eller bilder med mye tekst.
              </div>
            </>
          ) : isPreparingFile ? (
            <>
              <div className="flex size-14 items-center justify-center rounded-full bg-purple-100 text-purple-800">
                <LoaderCircle className="size-6 animate-spin" />
              </div>
              <div className="mt-4 text-sm font-semibold sm:text-base">Klargjør fil</div>
              <div className="mt-2 max-w-[28rem] text-xs text-warm-gray-800 sm:text-sm">
                Vi sjekker fila og gjør store bilder mindre før opplasting, så
                analysen kan gå raskere når du trykker på knappen.
              </div>
            </>
          ) : preparedFile ? (
            <>
              <div className="flex size-14 items-center justify-center rounded-full bg-green-100 text-green-700">
                <CheckCircle2 className="size-6" />
              </div>
              <div className="mt-4 text-sm font-semibold sm:text-base">Fil klar til analyse</div>
              <div className="mt-4 flex w-full max-w-full items-center gap-2 overflow-hidden rounded-full border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                <FileText className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {preparedFile.displayFile.name}
                </span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    onSelectedFile(null)
                  }}
                  className="shrink-0 rounded-full p-1 text-green-800 hover:bg-green-100 cursor-pointer"
                  aria-label="Fjern valgt fil"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              <div className="mt-3 text-xs text-warm-gray-700">
                {preparedFile.metadata.wasOptimized
                  ? supportsDragAndDrop
                    ? 'Fila er optimalisert for raskere opplasting. Slipp en ny fil her eller klikk for å bytte.'
                    : 'Fila er optimalisert for raskere opplasting. Klikk for å bytte.'
                  : supportsDragAndDrop
                    ? 'Slipp en ny fil her eller klikk for å bytte.'
                    : 'Klikk for å bytte.'}
              </div>
            </>
          ) : (
            <>
              <div className="flex size-14 items-center justify-center rounded-full bg-purple-100 text-purple-800">
                <Upload className="size-6" />
              </div>
              <div className="mt-4 text-sm font-semibold min-[700px]:hidden">
                Velg PDF eller bilde
              </div>
              <div className="mt-4 hidden text-sm font-semibold min-[700px]:block sm:text-base">
                Dra og slipp PDF eller bilde her
              </div>
              <div className="mt-2 text-xs text-warm-gray-800 min-[700px]:hidden">
                Trykk for å velge en fil fra enheten din
              </div>
              <div className="mt-2 hidden text-xs text-warm-gray-800 min-[700px]:block sm:text-sm">
                eller klikk for å velge en fil fra maskinen din
              </div>
              <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-warm-gray-300 bg-white px-3 py-2 text-sm font-medium shadow-[0_2px_4px_0_rgba(0,0,0,0.08)]">
                <Upload className="size-4" />
                Velg fil
              </div>
              <div className="mt-3 flex justify-center min-[700px]:hidden">
                <span className="text-xs uppercase tracking-[0.08em] text-warm-gray-600">
                  eller
                </span>
              </div>
              <div className="mt-3 flex justify-center min-[700px]:hidden">
                <label
                  htmlFor="report-card-camera-input"
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-warm-gray-400 bg-white px-4 py-2 text-sm font-medium shadow-[0_2px_4px_0_rgba(0,0,0,0.08)] hover:bg-warm-gray-100"
                >
                  <Camera className="size-4" />
                  Ta bilde
                </label>
              </div>
              <div className="mt-3 text-[11px] text-warm-gray-700 sm:text-xs">
                Støtter PDF, PNG, JPG, HEIC og WebP
              </div>
            </>
          )}
        </div>
      </label>
      <label className="mt-4 flex min-w-0 items-start gap-3 text-[11px] text-warm-gray-800 sm:text-xs">
        <Checkbox
          checked={hasAcceptedTerms}
          onCheckedChange={(checked) => onAcceptedTermsChange(checked === true)}
          className="mt-0.5"
          data-testid="report-card-terms-checkbox"
        />
        <span className="min-w-0">
          Jeg bekrefter at jeg forstår at filen sendes til OpenAI for analyse
          som del av tjenesten på vegne av ASK, og at jeg har lest{' '}
          <a
            href="https://www.askprivatist.no/personvern"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:no-underline"
          >
            personvernerklæringen
          </a>
          . Vi anbefaler at du skjuler fødselsnummer og andre
          personopplysninger før opplasting.
        </span>
      </label>
      <div className="mt-4 flex justify-center gap-3">
        <button
          type="button"
          onClick={onAnalyze}
          disabled={
            isPreparingFile || isImporting || !preparedFile || !hasAcceptedTerms
          }
          className="rounded-md bg-purple-700 px-4 py-2 text-sm font-medium text-white cursor-pointer hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-70 inline-flex items-center gap-2"
        >
          {(isPreparingFile || isImporting) && (
            <LoaderCircle className="size-4 animate-spin" />
          )}
          Les karakterene mine
        </button>
      </div>
      {error && <div className="mt-3 text-sm text-red-700">{error}</div>}
    </div>
  )
}
